import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Box,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DateFnsUtils from "@date-io/date-fns";
import { MuiPickersUtilsProvider, KeyboardDatePicker } from "@material-ui/pickers";
import { Title, useNotify } from "react-admin";
import { format, parseISO, isValid } from "date-fns";
import { apiUrl } from "../../dataProvider";

const MIN_DATE = parseISO("2025-12-09");

const useStyles = makeStyles((theme) => ({
  page: {
    padding: theme.spacing(2),
  },
  card: {
    maxWidth: 760,
    backgroundColor: "#ffffff",
    borderRadius: 8,
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  fieldLabel: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    marginBottom: 6,
  },
  dateField: {
    marginTop: 0,
    marginBottom: theme.spacing(1),
    width: 240,
    maxWidth: "100%",
  },
  priceField: {
    marginTop: 0,
  },
  actions: {
    marginTop: theme.spacing(3),
    display: "flex",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
  },
  emptyState: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(3, 0),
  },
}));

const formatDateParam = (date) => {
  if (!date || !isValid(date)) return null;
  return format(date, "yyyy-MM-dd");
};

const parseFloatInput = (value) => {
  if (value == null || value === "") return "";
  let next = String(value).replace(/[^\d.]/g, "");
  const firstDot = next.indexOf(".");
  if (firstDot !== -1) {
    next =
      next.slice(0, firstDot + 1) +
      next.slice(firstDot + 1).replace(/\./g, "");
  }
  return next;
};

const snapshotPrices = (prices = []) =>
  prices.map((item) => ({
    precious_metal_type_id: item.precious_metal_type_id,
    lbma_lme: item.lbma_lme == null ? "" : String(item.lbma_lme),
  }));

const PreciousLbmaLmePrices = () => {
  const classes = useStyles();
  const notify = useNotify();
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [payload, setPayload] = useState(null);
  const [originalPrices, setOriginalPrices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const fetchPrices = useCallback(
    async (date) => {
      const dateParam = formatDateParam(date);
      if (!dateParam) return;

      setLoading(true);
      try {
        const { data } = await axios.get(`${apiUrl}/precious-lbma-lme-prices`, {
          params: { date: dateParam },
        });
        const prices = Array.isArray(data?.prices) ? data.prices : [];
        setPayload({
          date: data?.date || dateParam,
          prices,
        });
        setOriginalPrices(snapshotPrices(prices));
      } catch (error) {
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          window.location.replace("/#/login");
          return;
        }
        notify(
          error?.response?.data?.message || "Failed to load LBMA/LME prices",
          { type: "warning" }
        );
        setPayload(null);
        setOriginalPrices([]);
      } finally {
        setLoading(false);
      }
    },
    [notify]
  );

  useEffect(() => {
    fetchPrices(selectedDate);
  }, [selectedDate, fetchPrices]);

  const isDirty = useMemo(() => {
    if (!payload?.prices?.length) return false;
    if (payload.prices.length !== originalPrices.length) return true;

    return payload.prices.some((item) => {
      const original = originalPrices.find(
        (entry) =>
          entry.precious_metal_type_id === item.precious_metal_type_id
      );
      return (
        String(original?.lbma_lme ?? "") !==
        String(item.lbma_lme == null ? "" : item.lbma_lme)
      );
    });
  }, [payload, originalPrices]);

  const handleDateChange = (date) => {
    if (!date || !isValid(date)) return;
    setSelectedDate(date);
    setPickerOpen(false);
  };

  const handlePriceChange = (preciousMetalTypeId, value) => {
    const nextValue = parseFloatInput(value);
    setPayload((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        prices: prev.prices.map((item) =>
          item.precious_metal_type_id === preciousMetalTypeId
            ? { ...item, lbma_lme: nextValue }
            : item
        ),
      };
    });
  };

  const handleUpdate = async () => {
    if (!payload || !isDirty || submitting) return;

    const dateParam = payload.date || formatDateParam(selectedDate);
    if (!dateParam) return;

    setSubmitting(true);
    try {
      await axios.put(`${apiUrl}/precious-lbma-lme-prices`, {
        date: dateParam,
        prices: payload.prices.map((item) => ({
          precious_metal_type_id: item.precious_metal_type_id,
          lbma_lme: String(item.lbma_lme ?? ""),
        })),
      });
      setOriginalPrices(snapshotPrices(payload.prices));
      notify("Prices updated successfully", { type: "info" });
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        window.location.replace("/#/login");
        return;
      }
      notify(
        error?.response?.data?.message || "Failed to update LBMA/LME prices",
        { type: "warning" }
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = async () => {
    if (!payload || submitting) return;

    const dateParam = payload.date || formatDateParam(selectedDate);
    if (!dateParam) return;

    setSubmitting(true);
    try {
      await axios.put(
        `${apiUrl}/reset-precious-lbma-lme-prices`,
        null,
        { params: { date: dateParam } }
      );
      notify("Prices reset successfully", { type: "info" });
      await fetchPrices(selectedDate);
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        window.location.replace("/#/login");
        return;
      }
      notify(
        error?.response?.data?.message || "Failed to reset LBMA/LME prices",
        { type: "warning" }
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetAll = async () => {
    if (submitting) return;

    setSubmitting(true);
    try {
      await axios.put(`${apiUrl}/reset-all-precious-lbma-lme-prices`);
      notify("All prices reset successfully", { type: "info" });
      await fetchPrices(selectedDate);
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        window.location.replace("/#/login");
        return;
      }
      notify(
        error?.response?.data?.message ||
          "Failed to reset all LBMA/LME prices",
        { type: "warning" }
      );
    } finally {
      setSubmitting(false);
    }
  };

  const busy = loading || submitting;
  const canReset = Boolean(payload) && !busy;

  return (
    <div className={classes.page}>
      <Title title="Precious LBMA/LME Prices" />
      <Card className={classes.card}>
        <CardContent>
          <Typography variant="h5" className={classes.title}>
            Precious LBMA/LME Prices
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography className={classes.fieldLabel}>Price Date</Typography>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <KeyboardDatePicker
                  className={classes.dateField}
                  open={pickerOpen}
                  onOpen={() => setPickerOpen(true)}
                  onClose={() => setPickerOpen(false)}
                  autoOk
                  disableToolbar
                  variant="inline"
                  inputVariant="outlined"
                  size="small"
                  format="MM/dd/yyyy"
                  margin="none"
                  id="precious-lbma-lme-date"
                  value={selectedDate}
                  onChange={handleDateChange}
                  minDate={MIN_DATE}
                  maxDate={new Date()}
                  disableFuture
                  disabled={busy}
                  InputLabelProps={{ shrink: false }}
                  PopoverProps={{
                    disableRestoreFocus: true,
                  }}
                  KeyboardButtonProps={{
                    "aria-label": "change date",
                  }}
                />
              </MuiPickersUtilsProvider>
            </Grid>

            {loading && (
              <Grid item xs={12}>
                <Box className={classes.loading}>
                  <CircularProgress size={28} />
                </Box>
              </Grid>
            )}

            {!loading &&
              payload?.prices?.map((item) => (
                <Grid item xs={12} sm={6} key={item.precious_metal_type_id}>
                  <Typography className={classes.fieldLabel}>
                    {item.name}
                  </Typography>
                  <TextField
                    className={classes.priceField}
                    variant="outlined"
                    size="small"
                    fullWidth
                    disabled={submitting}
                    value={item.lbma_lme ?? ""}
                    onChange={(event) =>
                      handlePriceChange(
                        item.precious_metal_type_id,
                        event.target.value
                      )
                    }
                    inputProps={{
                      inputMode: "decimal",
                      pattern: "[0-9.]*",
                    }}
                    placeholder="0.00"
                  />
                </Grid>
              ))}

            {!loading && payload && payload.prices?.length === 0 && (
              <Grid item xs={12}>
                <Typography className={classes.emptyState}>
                  No prices found for the selected date.
                </Typography>
              </Grid>
            )}
          </Grid>

          <Box className={classes.actions}>
            <Button
              variant="contained"
              color="primary"
              disabled={!isDirty || busy}
              onClick={handleUpdate}
            >
              Update
            </Button>
            <Button
              variant="outlined"
              color="primary"
              disabled={!canReset}
              onClick={handleReset}
            >
              Reset
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              disabled={busy}
              onClick={handleResetAll}
            >
              Reset All
            </Button>
          </Box>
        </CardContent>
      </Card>
    </div>
  );
};

export default PreciousLbmaLmePrices;
